import { encodeURIParams } from '@/_utils/encode-uri-params';
import { OrganizationResponse } from '@/common/models/response.model';
import { Organization } from '@/reservation/reservation.model';
import { ChangeDetectorRef, Component, OnInit, signal } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { DataBagService } from 'src/app/common/data-bag.service';
import { Data2Service } from 'src/app/common/data2.service';
import {
	CancellationStatusesListReservation,
	CommonStatusesListReservation,
	RejectedStatusesListReservation,
	Status,
} from 'src/app/common/enums/Status';
import { InfoboxService } from 'src/app/common/infobox/infobox.service';
import { DEFAULT_PARAMETERS, RESERVATION_LIST_COLUMS_DEF } from './bag-reservation-list-config';
import { ColumnsDef } from './bag-reservation-list-table/bag-reservation-list-table.component';
import {
	BagReservationListData,
	BagReservationListFilters,
	StatisticsLoadBeforeData,
} from './bag-reservation-list.model';
import { ProcessReservationData } from './process-reservation-list-data';
import { filtersToString } from '@/_utils/filters-to-string';
import { DatePipe } from '@angular/common';

export const DEFAULT_ORDERING = '-id';

@Component({
	selector: 'app-bag-reservation-list',
	templateUrl: './bag-reservation-list.component.html',
	styleUrls: ['./bag-reservation-list.component.scss'],
})
export class BagReservationListComponent implements OnInit {
	loading = signal(true);
	data: BagReservationListData[] = [];
	tableColumnsDef: ColumnsDef[] = RESERVATION_LIST_COLUMS_DEF;
	filters: BagReservationListFilters;
	parameters = DEFAULT_PARAMETERS;
	statistic = false;
	dataStatistic = [];

	page = signal(0);
	pageSize = signal(50);
	allItemsSize = signal(0);
	reservationCache = signal<{ [key: number]: BagReservationListData[] }>({});
	expandedPanel = signal(false);

	constructor(
		private readonly api: Data2Service,
		private readonly bag: DataBagService,
		private readonly infobox: InfoboxService,
		private readonly datePipe: DatePipe,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		// I know this sucks but I just rewritten that a little bit
		Promise.all([
			this.getResource('/location/basic/?limit=9999&location_kind=CR,TG'),
			this.getResource('/location/basic/?limit=9999&location_kind=CT'),
			this.getResource('/product/?limit=9999'),
			this.getResource<OrganizationResponse>('/organization/?limit=9999&is_oskar=true'),
			this.getResource<OrganizationResponse>('/organization/?limit=9999&is_oskar=false'),
			this.getResource('/transport/stop/?limit=9999'),
			this.getResource('/product/kind/?limit=9999'),
			this.getResource('/reservation/alert/?limit=9999'),
			this.getResource('/profile/all_users/?limit=9999'),
			this.getResource('/reservation/channel/?limit=9999'),
			this.getResource('/currency/?limit=9999'),
			this.getResource('/board/?limit=9999'),
			this.getResource('/offer/service/?limit=9999'),
			this.getResource('/reservation/season/?limit=9999'),
			this.getResource('/product/room/type/?limit=9999'),
			// eslint-disable-next-line  @typescript-eslint/no-explicit-any
		]).then((resultArr: any[]) => {
			this.parameters.locations = this.elementsIdsToString(resultArr[0]?.results);
			this.parameters.filtersLocations = this.elementsIdsToString(resultArr[1]?.results);
			this.parameters.products = this.elementsIdsToString(resultArr[2]?.results);
			if (resultArr[3].results) {
				this.parameters.organizations = this.elementsIdsToString<Organization>(resultArr[3]?.results);
			}
			if (resultArr[4].results) {
				this.parameters.agencies = this.elementsIdsToString<Organization>(resultArr[4]?.results);
			}
			this.parameters.transportStops = this.elementsIdsToString(resultArr[5]?.results);
			this.parameters.productKind = this.elementsIdsToString(resultArr[6]?.results);
			this.parameters.alerts = this.elementsIdsToString(resultArr[7]?.results);
			this.parameters.allUsers = this.elementsIdsToString(resultArr[8]?.results);
			this.parameters.channels = this.elementsIdsToString(resultArr[9]?.results);
			this.parameters.currency = this.elementsIdsToString(resultArr[10]?.results);
			this.parameters.boards = this.elementsIdsToString(resultArr[11]?.results);
			this.parameters.services = this.elementsIdsToString(resultArr[12]?.results);
			this.parameters.seasons = resultArr[13]?.results;
			this.parameters.roomTypes = resultArr[14]?.results;
			this.filters.ordering = DEFAULT_ORDERING;
			this.loadData();
		});
	}

	statisticLoadBefore(data: StatisticsLoadBeforeData): void {
		this.expandedPanel.set(false);
		this.statistic = data.show;
		this.dataStatistic = [];
		if (!data.show) {
			return;
		}
		if (!data.filters?.length || !data.filters === undefined) {
			data.filters = CommonStatusesListReservation;
		}
		const filtersWithoutStatus = Object.fromEntries(
			Object.entries(data.allFilters).filter(([key]) => key !== 'status' && key !== 'ordering'),
		);
		const statisticParms: string = this.getFiltersParams({
			...(filtersWithoutStatus as unknown as BagReservationListFilters),
			offset: 0,
			limit: 99999,
		});
		if (data.filters.length !== 1) {
			this.getAllStatistic();
		}
		data.filters.forEach((statusFilter) => {
			let status = '';
			if (statusFilter === Status.CANCELED) {
				CancellationStatusesListReservation.forEach((cancelStatus) => {
					status += `${status === '' ? '' : '&'}status=${cancelStatus}`;
				});
			} else if (statusFilter === Status.REJECTED) {
				RejectedStatusesListReservation.forEach((cancelStatus) => {
					status += `${status === '' ? '' : '&'}status=${cancelStatus}`;
				});
			} else {
				status = `status=${statusFilter}`;
			}
			this.dataStatistic.push({
				kind: statusFilter,
				data: null,
			});
			this.bag.get(`/reservation/reservation_statistics/?${status}&${statisticParms}`, (result) => {
				this.dataStatistic.find((element) => element.kind === statusFilter).data = { ...result };
				this.cdr.detectChanges();
			});
		});
	}

	filtersSelected(data: BagReservationListFilters): void {
		this.filters = data;
		this.reservationCache.set({});
		this.page.set(0);
		this.allItemsSize.set(0);
		this.loadData();
	}

	pageChanged(page: PageEvent): void {
		this.page.set(page.pageIndex);
		this.pageSize.set(page.pageSize);
		this.loadData();
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	sortBy(sortedBy: string): void {
		this.filters.ordering = sortedBy;
		this.reservationCache.set({});
		this.loadData();
	}

	private getAllStatistic(): void {
		const statisticParms: string = this.getFiltersParams({
			...this.filters,
			offset: 0,
			limit: 99999,
		});
		this.dataStatistic.push({
			kind: 'ALL',
			data: null,
		});
		this.bag.get(`/reservation/reservation_statistics/?${statisticParms}`, (result) => {
			this.dataStatistic.find((element) => element.kind === 'ALL').data = {
				...result,
			};
		});
	}

	private getFiltersParams(filters = this.filters): string {
		return encodeURIParams<BagReservationListFilters>(filters);
	}

	private getResource<T>(url: string): Promise<T> {
		return new Promise((resolve, reject) => {
			this.bag.get(url, (result, error) => {
				if (result) {
					resolve(result);
				} else {
					reject(error);
					throw new Error(JSON.stringify(error));
				}
			});
		});
	}

	private elementsIdsToString<T extends { id: unknown }>(array: T[]): T[] {
		return array?.map((obj) => ({
			...obj,
			id: obj.id.toString(),
		}));
	}

	private loadData(): void {
		this.filters.limit = this.pageSize();
		this.filters.offset = this.page() * this.pageSize();
		const params = this.getFiltersParams();
		if (this.reservationCache()[this.page()]) {
			this.data = this.reservationCache[this.page()];
			return;
		}
		this.loading.set(true);
		this.bag.get('/reservation/?' + params, (result, error) => {
			if (error) {
				this.loading.set(false);
				return;
			}
			this.allItemsSize.set(result.count);
			this.data = new ProcessReservationData(this.datePipe).processReservationListData(result);
			this.reservationCache.set({ ...this.reservationCache(), [this.page()]: this.data });
			this.loading.set(false);
		});
	}
}
